


/* ==========================================================================
   UI HELPERS & DRAWING UTILITIES (/database/js/ui-helpers.js)
   ========================================================================== */

import { escapeHtml, getDimensionToken, generateInlineNoteToggle, formatCaseBrandTitle, slugifyUrlSegment, caseRouteSlug, parseSpecNum } from '../utils.js?v=20260819-audit2';
import { state, getCaseModeSelection, getCaseStateKey } from '../state.js?v=20260819-audit2';
import { hasMoboFast, getEffectiveSpecs } from '../compatibility-engine.js?v=20260819-audit2';
import { normalizePsuFormFactor, resolvePsuFormFactorFromCatalog } from './hardware-index.js?v=20260819-audit2';

/**
 * Searches object keys for matching fields with flexible casing/key sanitization.
 */
export function findHwVal(obj, fields) {
  if (!obj || typeof obj !== 'object') return '';
  for (const f of fields) {
    const cf = f.toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        const ck = k.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (ck === cf) return obj[k];
      }
    }
  }
  for (const f of fields) {
    const cf = f.toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        const ck = k.toLowerCase().replace(/[^a-z0-9]/g, '');
        if ((cf === 'name' || cf === 'model' || cf === 'cooler' || cf === 'gpu' || cf === 'mobo' || cf === 'board') && 
            (ck.includes('brand') || ck.includes('mfr') || ck.includes('manufacturer'))) {
          continue;
        }
        if (ck.includes(cf)) return obj[k];
      }
    }
  }
  return '';
}

/**
 * Parses and formats hardware object properties into a standardized display title.
 */
export function getHwName(item, type) {
  if (!item) return '';
  if (type === 'case') return formatCaseBrandTitle(item.name || 'SFF Case');
  if (typeof item === 'string') {
    return item.replace(/\s*\(\d+(\.\d+)?x\d+(\.\d+)?x\d+(\.\d+)?mm\)$/i, '').trim();
  }
  const brand = String(findHwVal(item, ['brand', 'mfr', 'manufacturer'])).trim();
  if (type === 'gpu') {
    const model = String(findHwVal(item, ['model', 'gpu'])).trim();
    const variant = String(findHwVal(item, ['variant', 'submodel', 'edition', 'name', 'card_name'])).trim();
    let fullTitle = brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model;
    if (variant && !fullTitle.toLowerCase().includes(variant.toLowerCase())) {
      fullTitle += ` ${variant}`;
    }
    return fullTitle.trim();
  }
  if (type === 'mobo' || type === 'motherboard') {
    const model = String(findHwVal(item, ['model', 'name', 'board'])).trim();
    return brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model;
  }
  if (type === 'air' || type === 'cooler') {
    const cooler = String(findHwVal(item, ['cooler', 'model', 'variant', 'name'])).trim();
    return brand && !cooler.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${cooler}` : cooler;
  }
  if (type === 'aio') {
    const model = String(findHwVal(item, ['model', 'cooler', 'variant', 'name'])).trim();
    return brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model;
  }
  const name = String(findHwVal(item, ['model', 'cooler', 'gpu', 'name'])).trim();
  return brand && !name.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${name}` : name;
}


/* --------------------------------------------------------------------------
   DOCUMENTED BUILD FILTER NORMALIZATION

   Shared by Community Builds and Master Gallery. Filter values are derived only
   from builds returned for the active case; the global hardware catalog is used
   solely to resolve a submitted PSU model back to its form factor.
   -------------------------------------------------------------------------- */
const BUILD_FILTER_DIMENSIONS = Object.freeze([
  { key: 'mobo', label: 'MOTHERBOARD' },
  { key: 'psu', label: 'PSU' },
  { key: 'cooling', label: 'COOLING' },
  { key: 'mode', label: 'MODE' },
  { key: 'gpu', label: 'GPU' },
  { key: 'cooler', label: 'COOLER' }
]);

function normalizeBuildFilterIdentity(value) {
  return String(value || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function usableBuildFilterPart(part) {
  if (!part || part.is_custom) return false;
  const raw = String(part.name || '').trim().toUpperCase();
  return !['', 'NONE', 'N/A', 'NA', 'CUSTOM', 'UNKNOWN'].includes(raw);
}

function normalizeBuildMoboType(value) {
  const raw = String(value || '').trim().toUpperCase().replace(/_/g, '-');
  if (!raw) return '';
  if (raw === 'M-ATX' || raw === 'MICRO-ATX' || raw === 'MICRO ATX' || raw === 'MATX') return 'mATX';
  if (raw === 'MINI-ITX' || raw === 'MINI ITX' || raw === 'ITX') return 'ITX';
  if (raw === 'MINI-DTX' || raw === 'MINI DTX' || raw === 'DTX') return 'DTX';
  if (raw === 'ATX') return 'ATX';
  return '';
}

function normalizeBuildPsuType(value) {
  return normalizePsuFormFactor(value);
}

function resolveBuildPsuType(buildPart) {
  return resolvePsuFormFactorFromCatalog(buildPart, state.hardwareGlobal);
}


function exactPartFilter(part, prefix = '') {
  if (!usableBuildFilterPart(part)) return null;
  const name = String(part.name || '').trim();
  const slug = String(part.slug || '').trim().toLowerCase();
  const identity = slug || normalizeBuildFilterIdentity(name).replace(/\s+/g, '-');
  if (!identity) return null;
  return { value: `${prefix}${identity}`, label: name };
}

function resolveModeLabel(modeId, caseObj) {
  const raw = String(modeId || '').trim();
  if (!raw) return '';
  const match = window.SFFContracts?.resolveModeRecord?.(caseObj?.modes || [], raw, { fallbackDefault: false })
    || (Array.isArray(caseObj?.modes)
      ? caseObj.modes.find(mode => String(mode?.mode_id || '') === raw)
      : null);
  return String(match?.mode_name || match?.name || raw).trim();
}

/**
 * Returns one normalized filter descriptor for a documented Community Build.
 * Exact GPU/cooler values are emitted only for catalog-resolved parts.
 */
export function getDocumentedBuildFilterDescriptor(build, caseObj = null) {
  const evidence = build?.evidence_config || {};
  const components = evidence.components || build?.load_config?.components || {};
  const descriptor = {};

  const moboType = normalizeBuildMoboType(evidence.mobo_form_factor || build?.load_config?.mobo_form_factor);
  if (moboType) descriptor.mobo = { value: moboType.toUpperCase(), label: moboType };

  const psuType = resolveBuildPsuType(components.psu || {});
  if (psuType) descriptor.psu = { value: psuType, label: psuType };

  const aio = components.aio || {};
  const air = components.cooler || {};
  const aioExact = exactPartFilter(aio, 'aio:');
  const airExact = exactPartFilter(air, 'air:');
  if (aioExact) {
    descriptor.cooling = { value: 'AIO', label: 'AIO' };
    descriptor.cooler = aioExact;
  } else if (airExact) {
    descriptor.cooling = { value: 'AIR', label: 'AIR' };
    descriptor.cooler = airExact;
  }

  const modeId = String(evidence.case_mode || build?.load_config?.case_mode || '').trim();
  if (modeId) descriptor.mode = { value: modeId, label: resolveModeLabel(modeId, caseObj) };

  const gpuExact = exactPartFilter(components.gpu || {}, 'gpu:');
  if (gpuExact) descriptor.gpu = gpuExact;

  return descriptor;
}

export function getDocumentedBuildFilterDimensions() {
  return BUILD_FILTER_DIMENSIONS.map(item => ({ ...item }));
}

/**
 * Builds option counts from the builds actually returned for this case. A row is
 * exposed only when there are at least two distinct recorded values.
 */
export function getDocumentedBuildFilterOptions(builds, caseObj = null) {
  const descriptors = (Array.isArray(builds) ? builds : []).map(build => getDocumentedBuildFilterDescriptor(build, caseObj));
  const dimensions = [];

  for (const dimension of BUILD_FILTER_DIMENSIONS) {
    const values = new Map();
    descriptors.forEach(descriptor => {
      const item = descriptor[dimension.key];
      if (!item?.value || !item?.label) return;
      const current = values.get(item.value) || { value: item.value, label: item.label, count: 0 };
      current.count += 1;
      values.set(item.value, current);
    });

    if (values.size > 1) {
      dimensions.push({
        ...dimension,
        options: Array.from(values.values()).sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
        })
      });
    }
  }

  return { descriptors, dimensions };
}

export function documentedBuildMatchesFilters(descriptor, activeFilters = {}) {
  for (const dimension of BUILD_FILTER_DIMENSIONS) {
    const selected = String(activeFilters?.[dimension.key] || 'all');
    if (!selected || selected === 'all') continue;
    if (String(descriptor?.[dimension.key]?.value || '') !== selected) return false;
  }
  return true;
}

/**
 * Pre-evaluates hardware items in combobox dropdowns against active case clearances.
 */
export async function batchPreScoreCombobox(comboboxId, hwType, activeCase = null) {
  const container = document.getElementById(comboboxId);
  if (!container) return;

  let targetCase = activeCase;
  if (!targetCase && state.selectedCaseIdx !== null && state.fetchedCasesGlobal && state.fetchedCasesGlobal[state.selectedCaseIdx]) {
    targetCase = state.fetchedCasesGlobal[state.selectedCaseIdx];
  }

  if (!targetCase) {
    // Most root-feed sessions have never pre-scored this dropdown, so avoid two
    // full scans of thousands of options just to remove attributes that do not exist.
    if (!container.dataset.sffPrescoreKey) return;
    container.querySelectorAll('.combobox-ai-badge').forEach(b => b.remove());
    container.querySelectorAll('.combobox-item').forEach(item => item.removeAttribute('data-ai-fit'));
    delete container.dataset.sffPrescoreKey;
    return;
  }

  const items = container.querySelectorAll('.combobox-item');
  if (!items.length) return;

  const activeModeId = getCaseModeSelection(targetCase, 'default');
  const effectiveSpecs = getEffectiveSpecs(targetCase, activeModeId);
  const prescoreKey = [
    getCaseStateKey(targetCase), activeModeId, hwType,
    effectiveSpecs.maxCPU, effectiveSpecs.maxGPU, effectiveSpecs.maxGPUT, effectiveSpecs.maxAIO
  ].join('|');
  if (container.dataset.sffPrescoreKey === prescoreKey) return;

  const maxCpu = effectiveSpecs.maxCPU || 9999;
  const maxGpuL = effectiveSpecs.maxGPU || 9999;
  const maxGpuT = effectiveSpecs.maxGPUT || 9999;
  const maxAio = effectiveSpecs.maxAIO || 0;

  const itemArray = Array.from(items);

  for (const item of itemArray) {
    let isFit = true;

    if (hwType === 'gpu') {
      const gpuL = parseSpecNum(item.getAttribute('data-gpu-len'), 304);
      const gpuT = parseSpecNum(item.getAttribute('data-gpu-thick'), 40);
      if (gpuL > maxGpuL || gpuT > maxGpuT) isFit = false;
    } else if (hwType === 'air') {
      const cpuH = parseSpecNum(item.getAttribute('data-val'), 47);
      if (cpuH > maxCpu) isFit = false;
    } else if (hwType === 'aio') {
      const rad = parseSpecNum(item.getAttribute('data-rad-size'), 240);
      if (maxAio === 0 || rad > maxAio) isFit = false;
    } else if (hwType === 'mobo') {
      const moboFF = (item.getAttribute('data-form-factor') || 'ITX').toUpperCase();
      if (moboFF.includes('MATX')) {
        isFit = hasMoboFast(targetCase, 'MATX') || hasMoboFast(targetCase, 'ATX');
      } else {
        isFit = hasMoboFast(targetCase, 'ITX');
      }
    }

    item.setAttribute('data-ai-fit', isFit ? 'true' : 'false');

    let badge = item.querySelector('.combobox-ai-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'combobox-ai-badge';
      item.appendChild(badge);
    }

    badge.className = `combobox-ai-badge ${isFit ? 'fit-pass' : 'fit-fail'}`;
    badge.textContent = `${isFit ? '100%' : '0%'} ${isFit ? 'FIT' : 'NO FIT'}`;
  }
  container.dataset.sffPrescoreKey = prescoreKey;
}

/**
 * Validates presence of extra specs metadata.
 */
export function hasValidExtraSpecs(c) { 
  return !!(c && (c.seller || c.priceUSD || c.footprint)); 
}

/**
 * Generates technical specifications grid drawer HTML.
 */
export function generateTechnicalSpecsDrawerHtml(c, caseIdx) {
  const notes = c.specNotes || {};
  const slug = caseRouteSlug(c);
  const rawDisplay = (value, fallback = 'UNKNOWN') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text === '' ? fallback : text;
  };
  const yesNoUnknown = (value) => {
    const normalized = String(value ?? '').trim().toUpperCase();
    if (normalized === 'Y' || normalized === 'YES' || normalized === 'TRUE' || normalized === '1') return 'YES';
    if (normalized === 'N' || normalized === 'NO' || normalized === 'FALSE' || normalized === '0') return 'NO';
    return 'UNKNOWN';
  };
  const priceRaw = String(c.priceUSD ?? '').trim();
  const priceText = !priceRaw || priceRaw === '0' ? 'N/A' : (priceRaw.startsWith('$') ? priceRaw : `$${priceRaw}`);
  const dims = c.caseDimensions ? [0, 1, 2].map(i => `${getDimensionToken(c.caseDimensions, i).toUpperCase()} MM`) : ['UNKNOWN', 'UNKNOWN', 'UNKNOWN'];

  return `
    <div class="drawer-specs-grid font-mono">
      <div class="drawer-metric-item"><span class="drawer-metric-label">Seller / Mfr</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.seller, 'DIRECT'))}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">Price (USD)</span><span class="drawer-metric-value">${escapeHtml(priceText)}${generateInlineNoteToggle(notes, 'priceUSD', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">Case Material</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.material))}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">Footprint</span><span class="drawer-metric-value">${escapeHtml(c.footprint ? `${c.footprint} CM²` : 'UNKNOWN')}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">Case Length</span><span class="drawer-metric-value">${escapeHtml(dims[0])}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">Case Width</span><span class="drawer-metric-value">${escapeHtml(dims[1])}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">Case Height</span><span class="drawer-metric-value">${escapeHtml(dims[2])}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">PCIe Slots</span><span class="drawer-metric-value">${escapeHtml(c._known?.pcie ? rawDisplay(c.pcieSlots || c._pcie) : 'UNKNOWN')}${generateInlineNoteToggle(notes, 'pcie', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">GPU Riser</span><span class="drawer-metric-value">${escapeHtml(yesNoUnknown(c.gpuRiser))}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">USB-C I/O</span><span class="drawer-metric-value">${escapeHtml(yesNoUnknown(c.usbC))}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">USB-A 3.2 G1</span><span class="drawer-metric-value">${escapeHtml(yesNoUnknown(c.usbA3))}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">USB-A 2.0 I/O</span><span class="drawer-metric-value">${escapeHtml(yesNoUnknown(c.usbA2))}${generateInlineNoteToggle(notes, 'usbA2', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">3.5mm Jack I/O</span><span class="drawer-metric-value">${escapeHtml(yesNoUnknown(c.jack35))}${generateInlineNoteToggle(notes, 'jack35', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">60mm Fans</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.fans60))}${generateInlineNoteToggle(notes, 'fans60', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">80mm Fans</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.fans80))}${generateInlineNoteToggle(notes, 'fans80', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">92mm Fans</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.fans92))}${generateInlineNoteToggle(notes, 'fans92', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">120mm Fans</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.fans120))}${generateInlineNoteToggle(notes, 'fans120', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">140mm Fans</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.fans140))}${generateInlineNoteToggle(notes, 'fans140', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">2.5&quot; Drives</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.drives25))}${generateInlineNoteToggle(notes, 'drives25', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">3.5&quot; Drives</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.drives35))}${generateInlineNoteToggle(notes, 'drives35', slug, caseIdx)}</span></div>
      <div class="drawer-metric-item"><span class="drawer-metric-label">5.25&quot; Drives</span><span class="drawer-metric-value">${escapeHtml(rawDisplay(c.drives525))}${generateInlineNoteToggle(notes, 'drives525', slug, caseIdx)}</span></div>
    </div>`;
}

/**
 * Draws 2D micro-blueprint graphics onto card canvas elements.
 */
export function renderMicroBlueprint(canvas, caseObj, userCpu, userGpuLen, themeColors, spineStep = null, activeSpecs = null) {
  try {
    if (!caseObj || !canvas) return false; 
    const ctx = canvas.getContext('2d'); 
    if (!ctx) return false;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth || Math.floor(canvas.getBoundingClientRect().width) || 0; 
    if (w <= 0) return false;

    const h = 46;

    canvas.width = Math.floor(w * dpr); 
    canvas.height = Math.floor(h * dpr); 
    ctx.scale(dpr, dpr); 
    ctx.clearRect(0, 0, w, h);

    const mid = w / 2;
    const effSpecs = activeSpecs || getEffectiveSpecs(caseObj, getCaseModeSelection(caseObj, 'default'));

    // activeSpecs is the same final constraint object used by the verdict.
    const maxCpu = effSpecs.maxCPU;
    const maxGpu = effSpecs.maxGPU;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)'; 
    ctx.strokeRect(10, 6, mid - 20, 26); 
    ctx.strokeRect(mid + 10, 6, mid - 20, 26);

    ctx.fillStyle = themeColors.muted || '#9ca3af'; 
    ctx.font = 'bold 11px monospace, sans-serif'; 
    ctx.fillText(`CPU MAX: ${maxCpu >= 9999 ? 'OPEN' : maxCpu + 'MM'}`, 12, h - 6); 
    ctx.fillText(`GPU MAX: ${maxGpu >= 9999 ? 'OPEN' : maxGpu + 'MM'}`, mid + 12, h - 6);

    if (maxCpu > 0 && maxCpu < 9999) { 
      ctx.fillStyle = userCpu > maxCpu ? '#f43f5e' : (themeColors.blue || '#38bdf8'); 
      ctx.fillRect(13, 9, (mid - 26) * Math.min(userCpu / maxCpu, 1.0), 20); 
    }
    if (maxGpu > 0 && maxGpu < 9999 && !caseObj._isAPU) { 
      ctx.fillStyle = userGpuLen > maxGpu ? '#f43f5e' : '#f59e0b'; 
      ctx.fillRect(mid + 13, 9, (mid - 26) * Math.min(userGpuLen / maxGpu, 1.0), 20); 
    }
    return true;
  } catch(e) { 
    console.error("Blueprint execution crash intercepted:", e); 
    return false; 
  }
}
